import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import styles from "./incomeGrid.module.css";
import { ArrowRight, Filter, Home, Search } from "react-feather";
import Helmet from "react-helmet";
import Footer from "../../../../../components/Footer";
import { useAuth0 } from "@auth0/auth0-react";
import Loader from "../../../../../components/Loader";
import axios from "axios";
import { toast } from "react-smart-toaster";
import { TableLoader } from "../../../../../components/LoaderC";
import { Typography, TablePagination } from "@mui/material";

import GridFilters from "./components/GridFilters";
import GridCards from "./components/GridCards";
import dayjs from "dayjs";

const IncomeGrid = ({ handleView }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [contentLoading, setContentLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterredData, setFilterredData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [searchKey, setSearchKey] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const latestRequest = useRef(0);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(8);
  const [filterData, setFilterData] = useState({
    searchKey: "",
    property: "",
    apartment: "",
    payment_status: "",
    last_payment_date: null,
  });
  const [propertyList, setPropertyList] = useState([]);
  const [apartmentList, setApartmentList] = useState([]);

  useEffect(() => {
    const fetchAllProperties = async () => {
      const token = await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
      });
      axios(process.env.REACT_APP_PAYMENT_API_URL + "v1/property-list", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }).then((response) => {
        let propertyData = response.data?.data || [];
        setPropertyList(propertyData);

        if (localStorage.getItem("propertyFilterData")) {
          let filterPropertyId = localStorage.getItem("propertyFilterData");
          localStorage.removeItem("propertyFilterData");

          setFilterData((prev) => {
            return { ...prev, property: parseInt(filterPropertyId) };
          });

          let apartmentListTemp = [];
          propertyData?.map((i) => {
            if (i.value === parseInt(filterPropertyId)) {
              apartmentListTemp = i.apartments;
            }
            return null;
          });

          setApartmentList(apartmentListTemp || []);
        }

        if (localStorage.getItem("apartmentFilterData")) {
          let apartmentFilter = localStorage.getItem("apartmentFilterData");
          localStorage.removeItem("apartmentFilterData");
          apartmentFilter = parseInt(apartmentFilter);

          setFilterData((prev) => {
            return { ...prev, apartment: parseInt(apartmentFilter) };
          });
        }
      }).catch((error) => {
        console.error("[IncomeGrid] Failed to load property list for filter", error);
        toast.error(error?.response?.data?.message || "Unable to load properties. Please try again.");
      });
    };
    fetchAllProperties();
  }, [getAccessTokenSilently]);

  // Debounce search so we don't hit the API on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => setSearchKey(filterData.searchKey), filterData.searchKey ? 400 : 0);
    return () => clearTimeout(timer);
  }, [filterData.searchKey]);

  const { property, apartment, payment_status: paymentStatus, last_payment_date: lastPaymentDate } = filterData;

  useEffect(() => {
    const requestId = ++latestRequest.current;

    const getRentRolls = async () => {
      let params = {
        page: page + 1,
        per_page: perPage,
        search: searchKey.trim() || undefined,
        property_id: property !== "" ? property : undefined,
        apt_id: apartment !== "" ? apartment : undefined,
        status: paymentStatus !== "" ? paymentStatus : undefined,
      };
      if (Array.isArray(lastPaymentDate) && lastPaymentDate.length === 2) {
        params.start = dayjs(lastPaymentDate[0]).format("YYYY-MM-DD");
        params.end = dayjs(lastPaymentDate[1]).format("YYYY-MM-DD");
      }

      try {
        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

        const response = await axios.get(process.env.REACT_APP_PAYMENT_API_URL + "v1/rent-rolls-paginated", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: params,
        });

        // Ignore responses from requests that were superseded by newer filters/pages
        if (requestId !== latestRequest.current) return;

        setFilterredData(response.data?.data || []);
        setTotalCount(response.data?.meta?.total || 0);
        setLoadError(false);
      } catch (error) {
        if (requestId !== latestRequest.current) return;
        console.error("[IncomeGrid] Failed to load rent roll list", error);
        setLoadError(true);
        toast.error(error?.response?.data?.message || "Unable to load income. Please try again.");
      } finally {
        if (requestId === latestRequest.current)
          setContentLoading(false);
      }
    };

    getRentRolls();
  }, [getAccessTokenSilently, page, perPage, searchKey, property, apartment, paymentStatus, lastPaymentDate, reloadKey]);

  const selectProperty = (e) => {
    let apartmentListTemp = [];
    propertyList?.map((i) => {
      if (i.value === e) {
        apartmentListTemp = i.apartments;
      }
      return null;
    });

    setApartmentList(apartmentListTemp || []);
  };

  return (
    <div className={`${styles.BodyCon}`}>
      <div className={`${styles.mainCardDiv}`}>
        <div className={`${styles.mainCard}`}>
          <div className="control-pane">
            {contentLoading && (
              <div className="Loader TableLoader">
                <TableLoader />
              </div>
            )}

            <div className={`${styles.Row}`}>
              {!contentLoading && (
                <>
                  <GridFilters
                    setPage={setPage.bind(this)}
                    setFilterData={setFilterData}
                    setFilterOpen={setFilterOpen}
                    filterOpen={filterOpen}
                    filterData={filterData}
                    selectProperty={selectProperty}
                    propertyList={propertyList}
                    apartmentList={apartmentList}
                    handleView={handleView}
                  />

                  <div className={`${styles.NotesRow}`}>
                    {filterredData.map((item, index) => {
                      return (<GridCards key={item?.id} item={item} index={index} />);
                    })}

                    {filterredData.length > 0 && (
                      <div className="Pagination TablePaginationNew List">
                        <TablePagination
                          count={totalCount}
                          color="primary"
                          page={page}
                          rowsPerPage={perPage}
                          onPageChange={(e, v) => setPage(v)}
                          rowsPerPageOptions={[
                            { label: "4", value: 4 },
                            { label: "8", value: 8 },
                            { label: "16", value: 16 },
                            { label: "50", value: 50 },
                          ]}
                          onRowsPerPageChange={(e) => {
                            setPage(0);
                            setPerPage(e.target.value);
                          }}
                          labelRowsPerPage={"Payments per Page:"}
                        />
                      </div>
                    )}

                    {filterredData.length === 0 && loadError && (
                      <div className={`${styles.NoDataMain}`}>
                        <p className={`${styles.NoDataText}`}>Something went wrong</p>
                        <p className={`${styles.NoDataTextSub}`}>We couldn't load income.</p>
                        <button type="button" className={`${styles.RetryBu}`} onClick={() => setReloadKey((prev) => prev + 1)}>Retry</button>
                      </div>
                    )}

                    {filterredData.length === 0 && !loadError && (
                      <div className={`${styles.NoDataMain}`}>
                        <div className={`${styles.NoDataIMG}`}>
                          <img
                            src="/static/img/no-income.png"
                            alt="No Data"
                          />
                        </div>
                        <p className={`${styles.NoDataText}`}>
                          Income list is currently empty.
                        </p>
                        <p className={`${styles.NoDataTextSub}`}>
                          All your income entries will appear here once available.
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncomeGrid;
